import { useEffect, useState, type FormEvent } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { api } from "../../../lib/api.js";
import { useAuth } from "../../../lib/auth-context.js";

type TriggerKind = "keyword" | "comment" | "story_reply" | "postback";

interface Trigger {
  id: string;
  igAccountId: string | null;
  kind: TriggerKind;
  keyword: string;
  matchType: "exact" | "contains" | "regex";
  caseSensitive: boolean;
  mediaId: string | null;
  payload: string | null;
  body: { text: string } | null;
  flowId: string | null;
  priority: number;
  enabled: boolean;
  createdAt: string;
}

interface IgAccount {
  id: string;
  igUsername: string;
  status: string;
}

interface FlowRow {
  id: string;
  name: string;
  status: "draft" | "published" | "archived";
  publishedVersionId: string | null;
}

interface IgMedia {
  id: string;
  caption?: string;
  permalink?: string;
  media_type: string;
  media_url?: string;
  thumbnail_url?: string;
  timestamp: string;
}

const ANY_ACCOUNT = "__any__";
type ReplyMode = "text" | "flow";

export const Route = createFileRoute("/_app/settings/auto-replies")({
  component: AutoReplies,
});

function AutoReplies() {
  const { activeIgAccount } = useAuth();
  const [items, setItems] = useState<Trigger[]>([]);
  const [accounts, setAccounts] = useState<IgAccount[]>([]);
  const [flows, setFlows] = useState<FlowRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [kind, setKind] = useState<TriggerKind>("keyword");
  const [keyword, setKeyword] = useState("");
  const [matchType, setMatchType] = useState<Trigger["matchType"]>("contains");
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [mediaId, setMediaId] = useState("");
  const [payload, setPayload] = useState("");
  const [replyMode, setReplyMode] = useState<ReplyMode>("text");
  const [text, setText] = useState("");
  const [flowId, setFlowId] = useState<string>("");
  const [priority, setPriority] = useState(100);
  const [enabled, setEnabled] = useState(true);
  const [igAccountId, setIgAccountId] = useState<string>(ANY_ACCOUNT);
  const [busy, setBusy] = useState(false);
  // Comment-trigger media picker: list of recent posts for the selected
  // ig_account. Empty until kind=comment AND a real account is selected.
  const [media, setMedia] = useState<IgMedia[]>([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaError, setMediaError] = useState<string | null>(null);

  // Cycle 15.5: workspace-level per-contact cooldown.
  const [cooldownSecs, setCooldownSecs] = useState<number>(60);
  const [cooldownSaved, setCooldownSaved] = useState(false);
  const [cooldownBusy, setCooldownBusy] = useState(false);
  const [cooldownError, setCooldownError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .get<{ workspace: { autoReplyCooldownSeconds: number } }>(
        "/workspaces/me/settings",
      )
      .then((r) => setCooldownSecs(r.data.workspace.autoReplyCooldownSeconds))
      .catch(() => {
        // Non-fatal — form still works at default cooldown.
      });
  }, []);

  async function saveCooldown() {
    setCooldownBusy(true);
    setCooldownError(null);
    setCooldownSaved(false);
    try {
      await api.patch("/workspaces/me/settings", {
        autoReplyCooldownSeconds: cooldownSecs,
      });
      setCooldownSaved(true);
      setTimeout(() => setCooldownSaved(false), 2000);
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } }).response
        ?.data?.message;
      setCooldownError(msg ?? "Failed to save cooldown");
    } finally {
      setCooldownBusy(false);
    }
  }

  useEffect(() => {
    if (kind !== "comment" || !igAccountId || igAccountId === ANY_ACCOUNT) {
      setMedia([]);
      setMediaError(null);
      return;
    }
    void loadMedia(false);
  }, [kind, igAccountId]);

  async function loadMedia(forceRefresh: boolean) {
    if (kind !== "comment" || !igAccountId || igAccountId === ANY_ACCOUNT) return;
    setMediaLoading(true);
    setMediaError(null);
    try {
      const r = await api.get<{ media: IgMedia[]; cached: boolean }>(
        `/ig/accounts/${igAccountId}/media?limit=20${forceRefresh ? "&refresh=true" : ""}`,
      );
      setMedia(r.data.media);
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } }).response
        ?.data?.message;
      setMediaError(msg ?? "Failed to load posts. Reconnect the IG account if needed.");
      setMedia([]);
    } finally {
      setMediaLoading(false);
    }
  }

  useEffect(() => {
    if (!activeIgAccount) return;
    void loadAll();
  }, [activeIgAccount?.id]);

  async function loadAll() {
    setLoading(true);
    setError(null);
    try {
      const triggerParams = new URLSearchParams({ ig_account_id: activeIgAccount!.id });
      const [t, a, f] = await Promise.all([
        api.get<{ triggers: Trigger[] }>(`/triggers?${triggerParams}`),
        api.get<{ accounts: IgAccount[] }>("/ig/accounts"),
        api.get<{ flows: FlowRow[] }>("/flows"),
      ]);
      setItems(t.data.triggers);
      setAccounts(a.data.accounts);
      setFlows(f.data.flows);
    } catch {
      setError("Failed to load auto-replies.");
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setEditingId(null);
    setKind("keyword");
    setKeyword("");
    setMatchType("contains");
    setCaseSensitive(false);
    setMediaId("");
    setPayload("");
    setReplyMode("text");
    setText("");
    setFlowId("");
    setPriority(100);
    setEnabled(true);
    setIgAccountId(ANY_ACCOUNT);
  }

  function loadIntoForm(t: Trigger) {
    setEditingId(t.id);
    setKind(t.kind ?? "keyword");
    setKeyword(t.keyword);
    setMatchType(t.matchType);
    setCaseSensitive(t.caseSensitive);
    setMediaId(t.mediaId ?? "");
    setPayload(t.payload ?? "");
    if (t.flowId) {
      setReplyMode("flow");
      setFlowId(t.flowId);
      setText("");
    } else {
      setReplyMode("text");
      setText(t.body?.text ?? "");
      setFlowId("");
    }
    setPriority(t.priority);
    setEnabled(t.enabled);
    setIgAccountId(t.igAccountId ?? ANY_ACCOUNT);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const trimmed = keyword.trim();
    const trimmedText = text.trim();

    if (replyMode === "text" && !trimmedText) {
      setError("Reply message can't be empty.");
      setBusy(false);
      return;
    }
    if (replyMode === "flow" && !flowId) {
      setError("Pick a published flow to launch.");
      setBusy(false);
      return;
    }

    const body = {
      kind,
      // Postback triggers don't use keyword; the API tolerates "" for
      // non-keyword kinds. Send the raw value either way.
      keyword: trimmed,
      matchType,
      caseSensitive,
      mediaId: kind === "comment" && mediaId.trim() ? mediaId.trim() : null,
      payload: kind === "postback" ? payload.trim() || null : null,
      // EITHER body OR flowId — never both. The API enforces this too.
      body: replyMode === "text" ? { text: trimmedText } : null,
      flowId: replyMode === "flow" ? flowId : null,
      priority,
      enabled,
      igAccountId: igAccountId === ANY_ACCOUNT ? null : igAccountId,
    };
    try {
      if (editingId) {
        await api.patch(`/triggers/${editingId}`, body);
      } else {
        await api.post("/triggers", body);
      }
      resetForm();
      await loadAll();
    } catch (err: unknown) {
      type ApiErr = { response?: { data?: { error?: string; message?: string; errors?: Array<{ message: string }> } } };
      const data = (err as ApiErr).response?.data;
      setError(data?.error ?? data?.message ?? data?.errors?.[0]?.message ?? "Save failed.");
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm("Delete this auto-reply?")) return;
    setBusy(true);
    try {
      await api.delete(`/triggers/${id}`);
      await loadAll();
    } catch {
      setError("Delete failed.");
    } finally {
      setBusy(false);
    }
  }

  async function handleToggle(t: Trigger) {
    setBusy(true);
    try {
      await api.patch(`/triggers/${t.id}`, { enabled: !t.enabled });
      await loadAll();
    } catch {
      setError("Toggle failed.");
    } finally {
      setBusy(false);
    }
  }

  const publishedFlows = flows.filter((f) => f.status === "published");

  return (
    <main style={{ maxWidth: 760, margin: "0 auto", padding: "32px 24px" }}>
      <h1 style={{ fontSize: 32, fontWeight: 900, letterSpacing: "-0.02em", marginBottom: 8 }}>
        Auto-replies
      </h1>
      <p style={{ color: "var(--muted)", marginBottom: 24 }}>
        When someone DMs your Instagram account, auto-reply if their message
        matches a keyword. Reply with a single message, or launch a multi-step{" "}
        <Link to="/flows">flow</Link>. Highest priority wins; ties broken by
        oldest first.
      </p>

      {error && <div className="brut-error">{error}</div>}

      <div
        className="brut-card"
        style={{ padding: 14, marginBottom: 16, display: "grid", gap: 8 }}
      >
        <div style={{ fontWeight: 800, fontSize: 14 }}>Per-contact cooldown</div>
        <div style={{ fontSize: 12, color: "var(--muted)" }}>
          A contact can trigger at most one auto-reply (keyword / comment / story
          reply / postback) within this window. Sequences, broadcasts, and flow
          messages are NOT affected. <strong>Recommended: 60s.</strong> Lower
          values look bot-like to Meta and risk account restrictions.
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="number"
            min={0}
            max={86400}
            value={cooldownSecs}
            onChange={(e) =>
              setCooldownSecs(Math.max(0, Math.min(86400, Number(e.target.value) || 0)))
            }
            className="brut-input"
            style={{ maxWidth: 120 }}
          />
          <span style={{ fontSize: 13, color: "var(--muted)" }}>seconds</span>
          <button
            type="button"
            onClick={() => void saveCooldown()}
            disabled={cooldownBusy}
            className="brut-button is-secondary"
            style={{ padding: "6px 12px", fontSize: 12 }}
          >
            {cooldownBusy ? "Saving…" : cooldownSaved ? "Saved" : "Save"}
          </button>
          {cooldownSecs === 0 ? (
            <span style={{ fontSize: 12, color: "#b40000" }}>
              ⚠ disabled — every inbound can fire an auto-reply
            </span>
          ) : null}
        </div>
        {cooldownError ? (
          <div style={{ fontSize: 12, color: "#b40000" }}>{cooldownError}</div>
        ) : null}
      </div>

      <form onSubmit={handleSubmit} className="brut-card" style={{ marginBottom: 24, display: "grid", gap: 12 }}>
        <div style={{ fontWeight: 800, fontSize: 18 }}>
          {editingId ? "Edit auto-reply" : "New auto-reply"}
        </div>

        <div>
          <label className="brut-label">IG account</label>
          <select className="brut-input" value={igAccountId}
            onChange={(e) => setIgAccountId(e.target.value)}>
            <option value={ANY_ACCOUNT}>All accounts in this workspace</option>
            {accounts.filter((a) => a.status === "active").map((a) => (
              <option key={a.id} value={a.id}>@{a.igUsername}</option>
            ))}
          </select>
          {accounts.filter((a) => a.status === "active").length === 0 ? (
            <div style={{ fontSize: 12, color: "#b40000", marginTop: 4 }}>
              No active IG accounts. <Link to="/settings/instagram">Connect one</Link> first.
            </div>
          ) : null}
        </div>

        <div>
          <label className="brut-label">Trigger type</label>
          <select
            className="brut-input"
            value={kind}
            onChange={(e) => setKind(e.target.value as TriggerKind)}
          >
            <option value="keyword">Keyword (inbound DM)</option>
            <option value="comment">Comment on a post</option>
            <option value="story_reply">Story reply</option>
            <option value="postback">Button tap (postback)</option>
          </select>
          <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>
            {kind === "keyword"
              ? "Fires when an inbound DM matches the keyword."
              : kind === "comment"
                ? "Fires when someone comments on one of your posts. The bot DMs the commenter via Meta's private-replies path (works for 7 days)."
                : kind === "story_reply"
                  ? "Fires when someone replies to one of your IG stories. Leave keyword blank to match any reply."
                  : "Fires when a contact taps a quick-reply button you previously sent. Match the payload string set on the button."}
          </div>
        </div>

        {kind === "comment" ? (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <label className="brut-label">Post — optional</label>
              {igAccountId && igAccountId !== ANY_ACCOUNT ? (
                <button
                  type="button"
                  onClick={() => void loadMedia(true)}
                  className="brut-button is-secondary"
                  style={{ padding: "2px 8px", fontSize: 11 }}
                  disabled={mediaLoading}
                >
                  {mediaLoading ? "Refreshing…" : "Refresh"}
                </button>
              ) : null}
            </div>
            {!igAccountId || igAccountId === ANY_ACCOUNT ? (
              <div style={{ fontSize: 12, color: "var(--muted)" }}>
                Pick a specific IG account above to load your recent posts.
                Leaving "All accounts" matches comments on any post in this workspace.
              </div>
            ) : mediaError ? (
              <div style={{ fontSize: 12, color: "#b40000" }}>{mediaError}</div>
            ) : (
              <select
                className="brut-input"
                value={mediaId}
                onChange={(e) => setMediaId(e.target.value)}
                disabled={mediaLoading}
              >
                <option value="">Any post</option>
                {media.map((m) => (
                  <option key={m.id} value={m.id}>
                    {formatMediaLabel(m)}
                  </option>
                ))}
              </select>
            )}
            {mediaId && media.find((m) => m.id === mediaId) ? (
              <MediaPreview media={media.find((m) => m.id === mediaId)!} />
            ) : null}
          </div>
        ) : null}

        {kind !== "postback" ? (
          <div>
            <label className="brut-label">
              Keyword{kind === "story_reply" ? " (optional)" : " (or regex)"}
            </label>
            <input
              className="brut-input"
              required={kind === "keyword"}
              maxLength={500}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder={kind === "story_reply" ? "Leave blank to match any reply" : ""}
            />
          </div>
        ) : (
          <div>
            <label className="brut-label">Payload</label>
            <input
              className="brut-input"
              required
              maxLength={1000}
              value={payload}
              onChange={(e) => setPayload(e.target.value)}
              placeholder="Exact match against the button's payload string"
            />
          </div>
        )}

        {kind !== "postback" ? (
          <div style={{ display: "grid", gap: 12, gridTemplateColumns: "1fr 1fr" }}>
            <div>
              <label className="brut-label">Match</label>
              <select className="brut-input" value={matchType}
                onChange={(e) => setMatchType(e.target.value as Trigger["matchType"])}>
                <option value="exact">Exact</option>
                <option value="contains">Contains</option>
                <option value="regex">Regex</option>
              </select>
            </div>
            <div>
              <label className="brut-label">Case sensitive</label>
              <label style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 8 }}>
                <input type="checkbox" checked={caseSensitive}
                  onChange={(e) => setCaseSensitive(e.target.checked)} />
                <span>Match case exactly</span>
              </label>
            </div>
          </div>
        ) : null}

        <fieldset
          style={{
            border: "3px solid var(--ink)",
            padding: 12,
            margin: 0,
            background: "var(--paper)",
          }}
        >
          <legend style={{ fontWeight: 800, fontSize: 13, padding: "0 8px" }}>
            What happens when it matches
          </legend>
          <div style={{ display: "flex", gap: 16, marginBottom: 12 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="radio"
                name="replyMode"
                checked={replyMode === "text"}
                onChange={() => setReplyMode("text")}
              />
              <span>Send a single reply</span>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="radio"
                name="replyMode"
                checked={replyMode === "flow"}
                onChange={() => setReplyMode("flow")}
              />
              <span>Launch a flow</span>
            </label>
          </div>
          {replyMode === "text" ? (
            <div>
              <label className="brut-label">Reply message</label>
              <textarea
                className="brut-input"
                required={replyMode === "text"}
                maxLength={1000}
                rows={3}
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
            </div>
          ) : (
            <div>
              <label className="brut-label">Flow</label>
              {publishedFlows.length === 0 ? (
                <div style={{ fontSize: 13, color: "var(--muted)" }}>
                  No published flows yet.{" "}
                  <Link to="/flows">Create one</Link> and publish it, then come
                  back.
                </div>
              ) : (
                <select
                  className="brut-input"
                  value={flowId}
                  onChange={(e) => setFlowId(e.target.value)}
                  required={replyMode === "flow"}
                >
                  <option value="">Pick a published flow…</option>
                  {publishedFlows.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
        </fieldset>

        <div>
          <label className="brut-label">Priority</label>
          <input className="brut-input" type="number" min={0} max={1000}
            value={priority} onChange={(e) => setPriority(Number(e.target.value))}
            style={{ maxWidth: 160 }} />
          <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>
            Higher fires first when multiple triggers match the same inbound.
          </div>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input type="checkbox" checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)} />
          <span>Enabled</span>
        </label>

        {(() => {
          const warnings = computeRiskWarnings({
            kind,
            mediaId,
            keyword,
            matchType,
          });
          if (warnings.length === 0) return null;
          return (
            <div
              style={{
                padding: 10,
                background: "#fff7d6",
                border: "1px solid #d4a000",
                fontSize: 12,
                lineHeight: 1.4,
              }}
            >
              <div style={{ fontWeight: 800, marginBottom: 4 }}>
                ⚠ Risk warnings
              </div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          );
        })()}

        <div style={{ display: "flex", gap: 8 }}>
          <button type="submit" className="brut-button" disabled={busy}>
            {busy ? "Saving…" : editingId ? "Update" : "Create"}
          </button>
          {editingId && (
            <button type="button" className="brut-button is-secondary" onClick={resetForm}>
              Cancel
            </button>
          )}
        </div>
      </form>

      <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 12 }}>
        Existing auto-replies
      </h2>
      {loading ? (
        <p>Loading…</p>
      ) : items.length === 0 ? (
        <div className="brut-card" style={{ textAlign: "center", padding: 24 }}>
          <p style={{ margin: "0 0 8px", fontWeight: 700 }}>No auto-replies yet</p>
          <p style={{ margin: 0, fontSize: 14, color: "var(--muted)" }}>
            Auto-replies fire when an inbound DM matches a keyword,
            comment, story-reply, or postback. Add one above ↑ and the
            bot starts handling matching messages within seconds.
          </p>
        </div>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 12 }}>
          {items.map((t) => {
            const flow = t.flowId ? flows.find((f) => f.id === t.flowId) : null;
            return (
              <li key={t.id} className="brut-card" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700 }}>
                      {t.matchType} · {t.caseSensitive ? "case-sensitive" : "case-insensitive"} · priority {t.priority}
                    </div>
                    <div style={{ fontSize: 13, color: "var(--muted)" }}>
                      when message matches <code>{t.keyword}</code> →
                    </div>
                    {t.flowId ? (
                      <div style={{ marginTop: 4, fontSize: 14 }}>
                        launches flow{" "}
                        <Link
                          to={`/flows/$flowId/edit`}
                          params={{ flowId: t.flowId }}
                          style={{ fontWeight: 700 }}
                        >
                          {flow?.name ?? t.flowId}
                        </Link>
                      </div>
                    ) : (
                      <div style={{ marginTop: 4, fontSize: 14 }}>{t.body?.text}</div>
                    )}
                    <div style={{ marginTop: 4, fontSize: 11, color: "var(--muted)" }}>
                      Account: {t.igAccountId
                        ? `@${accounts.find((a) => a.id === t.igAccountId)?.igUsername ?? "?"}`
                        : "all accounts"}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button className="brut-button is-secondary" onClick={() => void handleToggle(t)} disabled={busy}>
                      {t.enabled ? "Disable" : "Enable"}
                    </button>
                    <button className="brut-button is-secondary" onClick={() => loadIntoForm(t)} disabled={busy}>
                      Edit
                    </button>
                    <button className="brut-button is-danger" onClick={() => void handleDelete(t.id)} disabled={busy}>
                      Delete
                    </button>
                  </div>
                </div>
                {!t.enabled && (
                  <div style={{ fontSize: 12, color: "var(--muted)", fontStyle: "italic" }}>
                    Disabled — won't fire.
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </main>
  );
}

function computeRiskWarnings(args: {
  kind: TriggerKind;
  mediaId: string;
  keyword: string;
  matchType: Trigger["matchType"];
}): string[] {
  const out: string[] = [];
  if (args.kind === "comment" && !args.mediaId.trim()) {
    out.push(
      "This comment trigger fires on EVERY post on the account. Consider pinning to a specific post — high-volume comment traffic is the #1 reason Meta restricts business accounts.",
    );
  }
  if (args.kind === "story_reply" && !args.keyword.trim()) {
    out.push(
      "Empty keyword + story reply = the bot DMs everyone who replies to a story, regardless of what they said. Add a keyword filter unless this is exactly what you want.",
    );
  }
  if (
    args.kind === "keyword" &&
    args.matchType === "contains" &&
    args.keyword.trim().length > 0 &&
    args.keyword.trim().length < 3
  ) {
    out.push(
      `Keyword "${args.keyword.trim()}" is very short and "contains" matches anywhere — likely to fire on unrelated messages. Consider exact match or a longer keyword.`,
    );
  }
  return out;
}

function formatMediaLabel(m: IgMedia): string {
  const date = new Date(m.timestamp).toLocaleDateString();
  const captionRaw = (m.caption ?? "").replace(/\s+/g, " ").trim();
  const caption = captionRaw ? captionRaw.slice(0, 60) + (captionRaw.length > 60 ? "…" : "") : "(no caption)";
  return `${date} · ${m.media_type} · ${caption}`;
}

function MediaPreview({ media }: { media: IgMedia }) {
  const thumb = media.thumbnail_url ?? media.media_url;
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginTop: 8, padding: 8, border: "1px solid var(--border)", background: "var(--bg-soft)" }}>
      {thumb ? (
        <img
          src={thumb}
          alt={media.caption ?? "post"}
          style={{ width: 64, height: 64, objectFit: "cover", border: "1px solid var(--border)" }}
        />
      ) : (
        <div style={{ width: 64, height: 64, background: "var(--border)", display: "grid", placeItems: "center", fontSize: 10 }}>
          {media.media_type}
        </div>
      )}
      <div style={{ minWidth: 0, fontSize: 12 }}>
        <div style={{ color: "var(--muted)" }}>{new Date(media.timestamp).toLocaleString()}</div>
        <div style={{ marginTop: 2, lineHeight: 1.3 }}>
          {(media.caption ?? "").slice(0, 200) || <em>(no caption)</em>}
        </div>
        {media.permalink ? (
          <a href={media.permalink} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, marginTop: 4, display: "inline-block" }}>
            View on Instagram ↗
          </a>
        ) : null}
      </div>
    </div>
  );
}
